/**
 * Validation schemas for income, expenses, categories and loans.
 */
import {
  EXPENSE_TREATMENTS,
  HMRC_EXPENSE_CATEGORIES,
  INCOME_TYPES,
  LOAN_TRANSACTION_TYPES,
  LOAN_TYPES,
  PAYMENT_METHODS,
  RATE_TYPES,
  values,
} from "../constants.ts";
import { dateOrder, v } from "../validation.ts";

const text = (max = 5000) => v.string({ max }).default("");
const positiveMoney = () => v.pence({ min: 1, fromPounds: true });

export const incomeSchema = v.object({
  propertyId: v.uuid(),
  tenancyId: v.uuid().optional(),
  incomeType: v.enum(values(INCOME_TYPES)),
  amountPence: positiveMoney(),
  receivedDate: v.date(),
  periodFromDate: v.date().optional(),
  periodToDate: v.date().optional(),
  description: text(500),
  reference: text(100),
  paymentMethod: v.enum(values(PAYMENT_METHODS)).optional(),
  notes: text(),
});

export const periodOrder = dateOrder<{ periodFromDate: string | null; periodToDate: string | null }>(
  "periodFromDate",
  "periodToDate",
  "Period end must be on or after period start",
);

export const expenseSchema = v.object({
  propertyId: v.uuid().optional(),
  ownerId: v.uuid().optional(),
  categoryId: v.uuid(),
  treatmentOverride: v.enum(values(EXPENSE_TREATMENTS)).optional(),
  amountPence: positiveMoney(),
  paidDate: v.date(),
  periodFromDate: v.date().optional(),
  periodToDate: v.date().optional(),
  contractorId: v.uuid().optional(),
  maintenanceId: v.uuid().optional(),
  description: v.string({ min: 1, max: 500 }),
  reference: text(100),
  paymentMethod: v.enum(values(PAYMENT_METHODS)).optional(),
  notes: text(),
});

export const expenseCategorySchema = v.object({
  name: v.string({ min: 1, max: 100 }),
  treatment: v.enum(values(EXPENSE_TREATMENTS)),
  hmrcCategory: v.enum(values(HMRC_EXPENSE_CATEGORIES)).optional(),
  sortOrder: v.int({ min: 0, max: 10000 }).default(0),
  isArchived: v.boolean(),
});

export const loanSchema = v.object({
  propertyId: v.uuid(),
  lender: v.string({ min: 1, max: 200 }),
  accountReference: text(100),
  loanType: v.enum(values(LOAN_TYPES)),
  originalAmountPence: v.pence({ min: 0, fromPounds: true }).optional(),
  startDate: v.date().optional(),
  termMonths: v.int({ min: 1, max: 600 }).optional(),
  rateType: v.enum(values(RATE_TYPES)).optional(),
  interestRatePercent: v.percent({ min: 0, max: 5000 }).optional(),
  productEndDate: v.date().optional(),
  redeemedDate: v.date().optional(),
  notes: text(),
});

export const loanTransactionSchema = v.object({
  transactionType: v.enum(values(LOAN_TRANSACTION_TYPES)),
  transactionDate: v.date(),
  interestPence: v.pence({ min: 0, fromPounds: true }).default(0),
  capitalPence: v.pence({ fromPounds: true }).default(0),
  feesPence: v.pence({ min: 0, fromPounds: true }).default(0),
  reference: text(100),
  notes: text(),
});
